import { endOfDay, isAfter, startOfDay } from 'date-fns';
import {
  AppointmentTimelineEvent,
  DocumentTimelineEvent,
  EmailTimelineEvent,
  InteractionDirection,
  InteractionTimelineEvent,
  NoteTimelineEvent,
  RelatedEntry,
  TaskTimelineEvent,
  TimelineEvent,
} from '../../../models';
import { BaseTimeline, Creator, KeyAndDisplay } from '../models';
import { ConfigurationMapper } from './configuration';

export abstract class BaseTimelineMapper<
  Source,
  Target,
> extends ConfigurationMapper<Source, Target> {
  mapDirection(source: BaseTimeline): InteractionDirection | undefined {
    if (source.Direction === 1) {
      return 'in';
    } else if (source.Direction === 2) {
      return 'out';
    }
    return 'none';
  }

  mapWith(source: BaseTimeline): RelatedEntry | undefined {
    if (source.AbEntries?.length) {
      return this.mapRelatedEntry('addressBook', source.AbEntries[0]);
    } else if (source.Leads?.length) {
      return this.mapRelatedEntry('lead', source.Leads[0]);
    }
    return undefined;
  }

  mapWiths(source: BaseTimeline): RelatedEntry[] | undefined {
    if (source.AbEntries?.length) {
      return source.AbEntries.map((x) =>
        this.mapRelatedEntry('addressBook', x),
      );
    } else if (source.Leads?.length) {
      return source.Leads.map((x) => this.mapRelatedEntry('lead', x));
    }
    return [];
  }

  mapRegarding(source: BaseTimeline): RelatedEntry | undefined {
    if (source.With?.Case) {
      return this.mapRelatedCase(source.With.Case);
    } else if (source.With?.Opportunity) {
      return this.mapRelatedOpportunity(source.With.Opportunity);
    }
    return undefined;
  }

  mapAppointment(
    event: TimelineEvent,
    source: BaseTimeline & Creator,
  ): AppointmentTimelineEvent {
    const isDone = source.Completed?.Value;
    const hasTime = isAfter(event.date.value, startOfDay(event.date.value));
    return {
      ...event,
      type: 'appointment',
      isDone,
      isOverdue:
        !isDone &&
        isAfter(
          new Date(),
          hasTime ? event.date.value : endOfDay(event.date.value),
        ),
      hasTime,
      attendees: source.Users?.length
        ? source.Users.filter(
            (user) => user.Key.Value !== source.Creator?.Value,
          ).map(this.mapRelatedUser)
        : [],
    };
  }

  mapNote(event: TimelineEvent, source: BaseTimeline): NoteTimelineEvent {
    return {
      ...event,
      type: 'note',
      noteType: source.NoteType.Value,
    };
  }
  mapUsers(source: BaseTimeline): RelatedEntry[] {
    return source.Users.map((x) =>
      this.mapRelatedEntry('user', {
        Key: x.Key.Value,
        DisplayValue: x.DisplayValue,
      } as KeyAndDisplay),
    );
  }
  mapTask(event: TimelineEvent, source: BaseTimeline): TaskTimelineEvent {
    const isDone = source.Completed?.Value;
    const hasTime = isAfter(event.date.value, startOfDay(event.date.value));
    return {
      ...event,
      type: 'task',
      isDone,
      isOverdue:
        !isDone &&
        isAfter(
          new Date(),
          hasTime ? event.date.value : endOfDay(event.date.value),
        ),
      hasTime,
      assignedTo: source.Users?.length
        ? source.Users.map(this.mapRelatedUser)
        : [],
      isWorkflowActivity: !!source.WorkflowInstanceKey,
    };
  }

  mapEmail(event: TimelineEvent, source: BaseTimeline): EmailTimelineEvent {
    return {
      ...event,
      type: 'email',
      attachments:
        source.Attachment && source.FileNames?.length ? source.FileNames : [],
    };
  }

  mapDocument(
    event: TimelineEvent,
    source: BaseTimeline,
  ): DocumentTimelineEvent {
    return {
      ...event,
      type: 'document',
      file: source.FileNames?.length ? source.FileNames[0] : '',
      documentType: source.DocumentType.Value,
    };
  }

  mapInteraction(
    event: TimelineEvent,
    source: BaseTimeline,
  ): InteractionTimelineEvent {
    return {
      ...event,
      interaction: this.mapInteractionType(source.Type),
      direction: this.mapDirection(source),
    };
  }
}
